import { movSitesV2 } from '@metservicedev/platforms-services-interface';
import type { ISite, ISiteCreateEventBody } from '@metservicedev/platforms-services-interface/src/services/movSites/MovSitesV2Model';
import { aggregatePaginationResponse } from '@metservicedev/platforms-services-interface/src/utils/asyncGeneratorForPagination';
// biome-ignore lint/style/useImportType: <explanation>
import React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { toHash } from '@mono/to-hash';
import { usePlatStageContext } from '@mono/use-plat-stage';

import * as sitesApi from './sitesApi';

interface SitesContext {
    isSitesLoading: boolean;
    sitesLoaded: boolean;
    sites: ISite[];
    fetchAllSites(): void;
    fetchSite: (siteId: string) => Promise<ISite | undefined>;
    createSite(data: ISiteCreateEventBody): Promise<ISite | undefined>;
    updateSite(params: { data: ISite; movSiteId: string }): void;
    deleteSite(movSiteId: string): void;
    key: string;
}
const SitesContext = createContext<SitesContext>({
    isSitesLoading: false,
    sitesLoaded: false,
    sites: [],
    fetchAllSites: () => undefined,
    fetchSite: () => Promise.resolve(undefined),
    createSite: async () => ({}) as ISite,
    updateSite: () => undefined,
    deleteSite: () => undefined,
    key: 'live, laugh, love',
});

const useSitesContext = () => useContext(SitesContext);

interface SitesContextProiderProps {
    children: React.ReactNode;
    metjwt: string;
    loadOnMount?: boolean;
}

const SitesContextProvider = ({ children, metjwt, loadOnMount = true }: SitesContextProiderProps) => {
    const { stage } = usePlatStageContext();
    const [sites, setSites] = useState<ISite[]>([]);
    const [isSitesLoading, setIsSitesLoading] = useState(false);
    const [sitesLoaded, setSitesLoaded] = useState(false);

    const currentOrg = localStorage.getItem('mip-organisation-id');
    const headers = {
        'x-current-org': currentOrg,
    };

    const fetchAllSites = async () => {
        if (sitesLoaded) {
            return;
        }
        setIsSitesLoading(true);
        const allSites = await aggregatePaginationResponse(movSitesV2.http.getAll({ stage, token: metjwt, headers }), (siteData) => siteData.sites);
        if (!allSites) throw new Error('Error whilst fetching all sites');
        setSites(allSites);
        setSitesLoaded(true);
        setIsSitesLoading(false);
    };

    useEffect(() => {
        if (!loadOnMount) return;
        fetchAllSites();
    }, []);

    const createSite = async (data: ISiteCreateEventBody) => {
        const site = await sitesApi.createSite(data, stage, metjwt, headers);
        if (!site) return;
        setSites((prevSites) => [...prevSites, site]);
        return site;
    };

    const deleteSite = async (movSiteId: string) => {
        await sitesApi.deleteSite(stage, movSiteId, metjwt, headers);
        console.log(`Deleting site: ${movSiteId}`);
        setSites((prevSites) => [...prevSites.filter((site) => site.movSiteId !== movSiteId)]);
    };

    const updateSite = async ({ data, movSiteId }: { data: ISite; movSiteId: string }) => {
        await sitesApi.updateSite({ data, stage, movSiteId, metjwt, headers });
        setSites((prevSites) => [...prevSites.filter((s) => s.movSiteId !== movSiteId), data]);
    };

    const fetchSite = async (movSiteId: string) => {
        const siteAlreadyInMemory = sites.find((s) => s.movSiteId === movSiteId);
        if (siteAlreadyInMemory) {
            return siteAlreadyInMemory;
        }
        const site = await movSitesV2.http.getById({
            stage,
            token: metjwt,
            movSiteId,
            headers,
        });
        if (site) setSites([...sites, site]);
        return site;
    };
    return (
        <SitesContext.Provider
            value={{
                sites,
                isSitesLoading,
                sitesLoaded,
                fetchAllSites,
                fetchSite,
                createSite,
                updateSite,
                deleteSite,
                key: toHash(sites.map((s) => s.movSiteId).join()),
            }}
        >
            {children}
        </SitesContext.Provider>
    );
};

export { SitesContextProvider, useSitesContext };
